/* ==========================================================================
   Ported from the standalone Instagram Scheduler project's httpClient.js.
   Originally a raw-fetch client with its own base-URL/env handling
   (import.meta.env is Vite-only) and its own JWT refresh retry logic;
   rewritten to go through the CRM's shared axios instance
   (src/api/axios.js) instead, so this tool automatically gets the same
   Bearer-token attachment and refresh-token retry as every other feature in
   the app -- its own refresh/token logic below is now unused dead weight
   removed, not reimplemented. Every *Api.js file's call shape (http.get/
   post/delete/patch/postForm/patchForm) is unchanged, so none of them
   needed edits beyond their path strings.
   ========================================================================== */
import api from "../../api/axios";

// Mounted at /api/instagram-scheduler on the backend.
const BASE = "/instagram-scheduler";

export class ApiError extends Error {
  constructor(message, status, payload) {
    super(message);
    this.status = status;
    this.payload = payload;
  }
}

function extractErrorMessage(payload) {
  if (!payload) return "Request failed.";
  if (typeof payload === 'string') return payload;
  if (typeof payload.detail === "string") return payload.detail;
  const firstKey = Object.keys(payload)[0];
  if (firstKey) {
    const val = payload[firstKey];
    const msg = Array.isArray(val) ? val[0] : val;
    return typeof msg === "string" ? msg : "Request failed.";
  }
  return "Request failed.";
}

async function request(path, { method = "GET", body, isFormData = false } = {}) {
  try {
    const res = await api.request({
      url: `${BASE}${path}`,
      method,
      timeout: path.startsWith("/analytics") ? 30000 : undefined,
      data: body,
      headers: isFormData ? { "Content-Type": "multipart/form-data" } : undefined,
    });
    return res.status === 204 || res.status === 205 ? null : res.data;
  } catch (err) {
    if (!err.response) throw new ApiError("Request failed.", 0, null);
    let message = extractErrorMessage(err.response.data);
    if (path.startsWith('/analytics') && err.response.status === 429 && /too many requests|too many analytics requests|rate.?limit/i.test(message)) {
      message = 'Insights are temporarily busy. Please try again shortly.';
    }
    const failure = new ApiError(message, err.response.status, err.response.data);
    failure.retryAfter = Math.max(0, Number(err.response.headers?.['retry-after']) || 0);
    failure.source = err.response.data?.source || (err.response.status === 429 ? 'application' : null);
    throw failure;
  }
}

export const http = {
  get: (path) => request(path, { method: "GET" }),
  // Authenticated binary download (PDF export) -- returns { blob, filename }.
  getBlob: async (path) => {
    try {
      const res = await api.request({ url: `${BASE}${path}`, method: "GET", responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      return { blob: res.data, filename: (cd.match(/filename="?([^";]+)"?/) || [])[1] || "metrics.pdf" };
    } catch (err) {
      if (!err.response) throw new ApiError("Request failed.", 0, null);
      let payload = null;
      try { payload = JSON.parse(await err.response.data.text()); } catch { /* not JSON */ }
      throw new ApiError(extractErrorMessage(payload), err.response.status, payload);
    }
  },
  post: (path, body) => request(path, { method: "POST", body }),
  delete: (path) => request(path, { method: "DELETE" }),
  patch: (path, body) => request(path, { method: "PATCH", body }),
  postForm: (path, formData) => request(path, { method: "POST", body: formData, isFormData: true }),
  patchForm: (path, formData) => request(path, { method: "PATCH", body: formData, isFormData: true }),
};
